const OPEN_POPUP_TRIGGER_SELECTOR = [
  '[role="combobox"][aria-expanded="true"]',
  '[aria-haspopup][aria-expanded="true"]',
  'input[aria-expanded="true"]',
].join(', ')

const NESTED_POPUP_SELECTOR = [
  OPEN_POPUP_TRIGGER_SELECTOR,
  '[role="listbox"]',
  '[role="menu"]',
].join(', ')

const OPEN_DIALOG_SELECTOR =
  '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]'

const OPEN_TOOLTIP_TRIGGER_SELECTOR =
  '[data-scope="tooltip"][data-part="trigger"][data-state="open"]'

const NON_TEXT_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
])

const getElementTarget = (event: Pick<Event, 'target'>): Element | null =>
  event.target instanceof Element ? event.target : null

/**
 * True when a key event comes from an open popup (expanded combobox or
 * popup trigger, listbox, menu) that should consume the key itself, so an
 * enclosing modal or panel must not also act on it.
 */
export function isKeyFromNestedPopup(event: Pick<Event, 'target'>): boolean {
  return getElementTarget(event)?.closest(NESTED_POPUP_SELECTOR) != null
}

/** True when a key event comes from a field where the user is typing. */
export function isKeyFromEditableField(event: Pick<Event, 'target'>): boolean {
  const target = getElementTarget(event)
  if (!target) return false
  if (target instanceof HTMLTextAreaElement) return true
  if (target instanceof HTMLSelectElement) return true
  if (target instanceof HTMLInputElement) {
    return !NON_TEXT_INPUT_TYPES.has(target.type)
  }
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable ||
      target.closest('[contenteditable=""], [contenteditable="true"]') != null)
  )
}

/**
 * True when the dialog receiving the key (the one holding the event target,
 * else the last open one) contains an expanded popup trigger.
 */
const dialogHasOpenPopup = (event: Pick<Event, 'target'>): boolean => {
  const openDialogs = document.querySelectorAll(OPEN_DIALOG_SELECTOR)
  const dialog =
    getElementTarget(event)?.closest(OPEN_DIALOG_SELECTOR) ??
    openDialogs[openDialogs.length - 1]
  return dialog?.querySelector(OPEN_POPUP_TRIGGER_SELECTOR) != null
}

/**
 * Pass as a dialog's `onEscapeKeyDown` so Escape while a custom nested popup
 * (e.g. a time picker listbox) is open closes only that popup. The popup owns
 * the key even when focus is elsewhere in the dialog, so the widget must close
 * itself on Escape (see `useEscapeToClosePopup`).
 */
export function preventEscapeFromNestedPopup(event: KeyboardEvent): void {
  if (
    isKeyFromNestedPopup(event) ||
    dialogHasOpenPopup(event) ||
    getElementTarget(event)?.closest(OPEN_TOOLTIP_TRIGGER_SELECTOR) != null
  ) {
    event.preventDefault()
  }
}
